import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { updateDashboardData } from "../../features/common/commonSlice";
import html2pdf from "html2pdf.js";
import { toast } from "react-toastify";
import { marked } from "marked";
import PanchayatiRajLogo from "../../assets/images/PanchayatiRajLogo.png";
import { disclaimerTranslations, en_translate_language_map } from "../../constants/disclaimerTranslations";
import { playGlobalAudio, subscribeAudio, toggleGlobalAudio } from "../../utils/audioManager";
import { playTTSPublic } from "../../utils/ttsService";
const FaFilePdf = require("react-icons/fa").FaFilePdf;
const FaPause = require("react-icons/fa").FaPause;
const FaPlay = require("react-icons/fa").FaPlay;

interface Meeting {
  meeting_date: string;
  meeting_type: string | null;
  meeting_title: string | null;
  minutes: string;
  language: string;
  task_id : string;
}
export interface AudioState {
  id: string | null;
  isPlaying: boolean;
  isLoading: boolean;
}

const MomPage: React.FC<{ finYear: string; meetingType: any }> = ({
  finYear,
  meetingType,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const dispatch = useDispatch();
  const currentClickedMenu = useSelector(
    (state: RootState) => state.common.currentClickedMenu
  );

  const dashboardData = useSelector(
    (state: RootState) => state.common.dashboardData
  );
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [audioState, setAudioState] = useState<AudioState>({
    id: null, // Start with null, as your interface expects
    isPlaying: false,
    isLoading: false,
  });
  useEffect(() => {
    const unsubscribe = subscribeAudio((id, playing) => {
      setAudioState((prev) => ({
        ...prev,
        id: id || "",
        isPlaying: playing,
        isLoading: false,
      }));
    });

    return () => unsubscribe();
  }, []);

  const zillaData = dashboardData["Zilla Panchayats"];
  const blockData = dashboardData["Block Panchayats"];
  const gramData = dashboardData["Gram Panchayats"];

  const localBodyCode = (() => {
    if (currentClickedMenu === "Zilla Panchayats") {
      return zillaData?.zp_lgd_code;
    } else if (currentClickedMenu === "Block Panchayats") {
      return blockData?.bp_lgd_code;
    } else if (currentClickedMenu === "Gram Panchayats") {
      return gramData?.gp_lgd_code;
    }
    return null;
  })();


  const handleBack = () => {
    if (currentClickedMenu === "Zilla Panchayats") {
      dispatch(updateDashboardData({ menu: "Zilla Panchayats", field: "zp_lgd_code", value: null }));
      dispatch(updateDashboardData({ menu: "Zilla Panchayats", field: "zp_name", value: "" }));
    } else if (currentClickedMenu === "Block Panchayats") {
      dispatch(updateDashboardData({ menu: "Block Panchayats", field: "bp_lgd_code", value: null }));
      dispatch(updateDashboardData({ menu: "Block Panchayats", field: "bp_name", value: "" }));
    } else if (currentClickedMenu === "Gram Panchayats") {
      dispatch(updateDashboardData({ menu: "Gram Panchayats", field: "gp_lgd_code", value: null }));
      dispatch(updateDashboardData({ menu: "Gram Panchayats", field: "gp_name", value: "" }));
    }
  };

  useEffect(() => {
    if (!localBodyCode) return;

    const fetchMom = async () => {
      try {
        const response = await axios.get(`${baseUrl}api/dashboard-minutes/`, {
          params: {
            fin_year: finYear,
            local_body_code: localBodyCode,
            meeting_type: meetingType.value || 0,
          },
        });

        setMeetings(response.data.meetings || []);
      } catch (error) {
        console.error("Error fetching MOM data:", error);
      }
    };

    fetchMom();
  }, [finYear, localBodyCode, meetingType, baseUrl]);

  const exportPDF = (
    minutes: string,
    meetingDate: string,
    meetingType: string | null,
    meetingTitle: string | null,
    lang: string = "english"
  ) => {
    if (!minutes) {
      toast.error("No content to export.");
      return;
    }

    const cleaned = minutes.replace(/\*\*/g, "").trim();
    const htmlContent = marked.parse(cleaned);

    const {
      state_name,
      zp_name,
      zp_lgd_code,
      bp_name,
      bp_lgd_code,
      gp_name,
      gp_lgd_code,
    } =
      currentClickedMenu === "Zilla Panchayats"
        ? zillaData || {}
        : currentClickedMenu === "Block Panchayats"
          ? blockData || {}
          : gramData || {};

    let langCode = "en";

    if (lang && lang.toLowerCase() !== "english") {
      const foundLang = Object.entries(en_translate_language_map).find(
        ([, name]) => name.toLowerCase() === lang.toLowerCase()
      );

      langCode = foundLang ? foundLang[0] : "en";
    }

    const englishDisclaimer = disclaimerTranslations["en"];
    const regionalDisclaimer = disclaimerTranslations[langCode];

    let disclaimerHTML = "";

    if (langCode === "en") {
      disclaimerHTML = `
      <div class="pdf-block disclaimer">
        <p><b class="text-danger">${englishDisclaimer.title}:</b> ${englishDisclaimer.text}</p>
      </div>
    `;
    } else {
      disclaimerHTML = `
      <div class="pdf-block disclaimer">
        <p><b class="text-danger">${regionalDisclaimer?.title}:</b> ${regionalDisclaimer?.text}</p>
        <p><b class="text-danger">${englishDisclaimer.title}:</b> ${englishDisclaimer.text}</p>
      </div>
    `;
    }

    const element = document.createElement("div");
    element.style.width = "100%";
    element.style.maxWidth = "794px";

    element.innerHTML = `

<link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500&display=swap" rel="stylesheet">

<style>

.pdf-root{
  font-family:
    'Noto Sans',
    'Noto Sans Devanagari',
    'Noto Sans Bengali',
    'Noto Sans Tamil',
    'Noto Sans Telugu',
    'Noto Sans Kannada',
    'Noto Sans Malayalam',
    'Noto Sans Gujarati',
    'Noto Sans Gurmukhi',
    'Noto Sans Oriya',
    'Noto Sans Ol Chiki',
    Arial,
    sans-serif;

  font-size:19px;
  line-height:1.7;
}

/* paragraphs */

.pdf-root p,
.pdf-root li{
  line-height:1.7;
  margin-bottom:6px;
  word-break:normal;
  overflow-wrap:anywhere;
  white-space:normal;
  page-break-inside:avoid;
  break-inside:avoid;
}

/* lists */

.pdf-root ul,
.pdf-root ol{
  padding-left:18px;
  page-break-inside:avoid;
}

/* headings */

.pdf-root h1,
.pdf-root h2,
.pdf-root h3,
.pdf-root h4{
  page-break-after:avoid;
}

/* images */

.pdf-root img{
  max-width:100%;
  page-break-inside:avoid;
}

/* blocks */

.pdf-block{
  page-break-inside:avoid;
  break-inside:avoid;
}

/* minutes section */

.minutes-content{
  line-height:1.7;
}

/* disclaimer */

.disclaimer{
  margin-top:8px;
}

</style>

<div class="pdf-root">

<!-- Header -->

<div class="pdf-block" style="display:flex; align-items:center; gap:7px; margin-bottom:10px;">
  <img src="${PanchayatiRajLogo}" alt="Logo" style="height:50px;" />

  ${state_name ? `<div>State: ${state_name}</div>` : ""}

  ${zp_name
        ? `<div>ZP: ${zp_name}${zp_lgd_code ? ` (${zp_lgd_code})` : ""}</div>`
        : ""
      }

  ${bp_name
        ? `<div>BP: ${bp_name}${bp_lgd_code ? ` (${bp_lgd_code})` : ""}</div>`
        : ""
      }

  ${gp_name
        ? `<div>GP: ${gp_name}${gp_lgd_code ? ` (${gp_lgd_code})` : ""}</div>`
        : ""
      }

</div>

<hr/>

<!-- Meeting Info -->
<div class="pdf-block">
  <div><b>Meeting Type:</b> ${meetingType || "-"}</div>
  <div><b>Meeting Title:</b> ${meetingTitle || "-"}</div>
  <div><b>Meeting Date:</b> ${meetingDate || "-"}</div>
</div>
<div class="minutes-content">
  ${htmlContent}
</div>
  ${disclaimerHTML}
</div>
`;

    const opt: any = {
      margin: [15, 10, 15, 10],
      filename: `MOM_${meetingDate}.pdf`,
      image: { type: "jpeg", quality: 0.98 },

      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true
      },

      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait"
      },

      pagebreak: {
        mode: ["css", "legacy"]
      }
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => toast.success("Exported as PDF"))
      .catch((err: any) => {
        console.error(err);
        toast.error("Failed to export PDF.");
      });
  };


  let chips: string[] = [];

  if (currentClickedMenu === "Zilla Panchayats") {
    if (zillaData?.state_name && zillaData?.state_code) {
      chips.push(`${zillaData.state_name} - ${zillaData.state_code}`);
    }
    if (zillaData?.zp_name && zillaData?.zp_lgd_code) {
      chips.push(`${zillaData.zp_name} - ${zillaData.zp_lgd_code}`);
    }
  }

  if (currentClickedMenu === "Block Panchayats") {
    if (blockData?.state_name && blockData?.state_code) {
      chips.push(`${blockData.state_name} - ${blockData.state_code}`);
    }
    if (blockData?.zp_name && blockData?.zp_lgd_code) {
      chips.push(`${blockData.zp_name} - ${blockData.zp_lgd_code}`);
    }
    if (blockData?.bp_name && blockData?.bp_lgd_code) {
      chips.push(`${blockData.bp_name} - ${blockData.bp_lgd_code}`);
    }
  }

  if (currentClickedMenu === "Gram Panchayats") {
    if (gramData?.state_name && gramData?.state_code) {
      chips.push(`${gramData.state_name} - ${gramData.state_code}`);
    }
    if (gramData?.zp_name && gramData?.zp_lgd_code) {
      chips.push(`${gramData.zp_name} - ${gramData.zp_lgd_code}`);
    }
    if (gramData?.bp_name && gramData?.bp_lgd_code) {
      chips.push(`${gramData.bp_name} - ${gramData.bp_lgd_code}`);
    }
    if (gramData?.gp_name && gramData?.gp_lgd_code) {
      chips.push(`${gramData.gp_name} - ${gramData.gp_lgd_code}`);
    }
  }

  return (
    <div className="mt-3">
      <div className="chip-container mb-3">
        <button className="back-btn" onClick={handleBack}>
          &larr; Back
        </button>

        {chips.map((chip, idx) => (
          <div key={idx} className="data-chip">
            {chip}
          </div>
        ))}
      </div>


      <div className="table-responsive">
        <table className="table table-striped mt-3">
          <thead className="thead-cus">
            <tr>
              <th className="left-radius">S.No.</th>
              <th className="no-radius">Meeting Date</th>
              <th className="no-radius">Meeting Type</th>
              <th className="no-radius">Meeting Title</th>
              <th className="text-center right-radius">Download PDF</th>
            </tr>
          </thead>
          <tbody>
            {meetings.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center">
                  No meetings found
                </td>
              </tr>
            ) : (
              meetings.map((meeting, index) => {
                const audioId = meeting.task_id;

                return (
                  <tr key={audioId}>
                    <td>{index + 1}</td>

                    <td>{meeting.meeting_date}</td>

                    <td>{meeting.meeting_type || "-"}</td>

                    <td>{meeting.meeting_title || "-"}</td>

                    <td className="text-center">
                      <div className="tts-pdf-container">

                        <FaFilePdf
                          className="pdf-icon"
                          onClick={() =>
                            exportPDF(
                              meeting.minutes,
                              meeting.meeting_date,
                              meeting.meeting_type,
                              meeting.meeting_title,
                              meeting.language
                            )
                          }
                        />

                        <button
                          className="tts-btn"
                          disabled={
                            audioState.isLoading &&
                            audioState.id === audioId
                          }
                          onClick={(e) => {
                            e.stopPropagation();

                            playTTSPublic({
                              text: meeting.minutes,
                              id: meeting.task_id,
                              lang: meeting.language,
                              languageMap: en_translate_language_map,
                              audioState,
                              setAudioState,
                              toggleGlobalAudio,
                              playGlobalAudio,
                            });
                          }}
                        >
                          {audioState.id === audioId ? (
                            audioState.isLoading ? (
                              <span className="loader-audio"></span>
                            ) : audioState.isPlaying ? (
                              <FaPause
                                className="pause-audio"
                                title="Pause"
                              />
                            ) : (
                              <FaPlay
                                className="play-audio"
                                title="Play"
                              />
                            )
                          ) : (
                            <FaPlay
                              className="play-audio"
                              title="Play"
                            />
                          )}
                        </button>

                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MomPage;
